import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  CustomerPayment,
  CreateCustomerPaymentData,
  CustomerPaymentsResponse,
  CustomerPaymentResponse,
  CustomerPaymentsListParams,
  ApplyCustomerPaymentAllocationsData,
  ReplaceCustomerPaymentAllocationsData,
  UpdateCustomerPaymentData,
  VoidCustomerPaymentData,
  SendCustomerPaymentData,
} from '../types/customerPayment';
import { parseContentDispositionFilename } from './contentDisposition';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildCustomerPaymentsUrl = (params?: CustomerPaymentsListParams) => {
  const query = new URLSearchParams();
  if (params?.customer_id) {
    query.set('customer_id', String(params.customer_id));
  }
  if (params?.unapplied !== undefined) {
    query.set('unapplied', String(params.unapplied));
  }
  const qs = query.toString();
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_ALL}${qs ? `?${qs}` : ''}`;
};

export const getCustomerPayments = async (
  params?: CustomerPaymentsListParams
): Promise<CustomerPayment[]> => {
  const response = await fetch(buildCustomerPaymentsUrl(params), {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customer payments');
  }

  const result: CustomerPaymentsResponse = await response.json();
  return result.data ?? [];
};

export const getAllCustomerPayments = async (): Promise<CustomerPayment[]> =>
  getCustomerPayments();

export const getCustomerPaymentById = async (id: number): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customer payment');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const createCustomerPayment = async (
  data: CreateCustomerPaymentData
): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.CREATE}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to record customer payment');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const applyCustomerPaymentAllocations = async (
  paymentId: number,
  data: ApplyCustomerPaymentAllocationsData
): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.APPLY_ALLOCATIONS}/${paymentId}/allocations`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to apply unapplied credit');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const updateCustomerPayment = async (
  paymentId: number,
  data: UpdateCustomerPaymentData
): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_BY_ID}/${paymentId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update customer payment');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const replaceCustomerPaymentAllocations = async (
  paymentId: number,
  data: ReplaceCustomerPaymentAllocationsData
): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_BY_ID}/${paymentId}/allocations`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update payment allocations');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const voidCustomerPayment = async (
  id: number,
  data: VoidCustomerPaymentData = {}
): Promise<CustomerPayment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.VOID}/${id}/void`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        void_reason: data.void_reason?.trim() || null,
      }),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void customer payment');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};

export const fetchCustomerPaymentPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.PDF}/${id}/pdf`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load payment receipt PDF');
  }

  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
    fallbackFilename ??
    `payment-${id}.pdf`;

  return { blob, filename };
};

export const downloadCustomerPaymentPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const { blob, filename } = await fetchCustomerPaymentPdf(id, fallbackFilename);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const sendCustomerPayment = async (
  id: number,
  data?: SendCustomerPaymentData
): Promise<CustomerPayment> => {
  const payload: SendCustomerPaymentData = {};
  if (data?.to?.trim()) payload.to = data.to.trim();
  if (data?.subject?.trim()) payload.subject = data.subject.trim();
  if (data?.message?.trim()) payload.message = data.message.trim();
  if (data?.cc != null) {
    if (Array.isArray(data.cc)) {
      const emails = data.cc.map((e) => e.trim()).filter(Boolean);
      if (emails.length === 1) payload.cc = emails[0];
      else if (emails.length > 1) payload.cc = emails;
    } else if (data.cc.trim()) {
      payload.cc = data.cc.trim();
    }
  }

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.SEND}/${id}/send`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to send payment receipt');
  }

  const result: CustomerPaymentResponse = await response.json();
  return result.data;
};
